/**
 * MagicBI Design System - Breakpoint Tokens
 * 
 * Responsive design breakpoints and media query utilities
 * for consistent layout across different screen sizes.
 */

// Breakpoint values (in pixels)
export const breakpointValues = {
  xs: 0,      // Extra small devices (phones)
  sm: 576,    // Small devices (large phones)
  md: 768,    // Medium devices (tablets)
  lg: 992,    // Large devices (desktops)
  xl: 1200,   // Extra large devices (large desktops)
  xxl: 1400,  // Extra extra large devices (very large screens)
} as const;

// Breakpoint names for convenience
export const breakpoints = Object.keys(breakpointValues) as Array<keyof typeof breakpointValues>;

// Container max-widths for each breakpoint
export const containerMaxWidths = {
  sm: '540px',
  md: '720px',
  lg: '960px',
  xl: '1140px',
  xxl: '1320px',
} as const;

// Grid system configuration
export const gridSystem = {
  // Number of columns in the grid
  columns: 12,
  
  // Grid gutters (spacing between columns)
  gutters: {
    xs: '16px',
    sm: '16px',
    md: '24px',
    lg: '24px',
    xl: '24px',
    xxl: '24px',
  },
  
  // Container padding
  containerPadding: {
    xs: '16px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '32px',
    xxl: '32px',
  },
} as const;

// Media query utilities
export const mediaQueries = {
  // Min-width media queries (mobile-first approach)
  up: {
    xs: `@media (min-width: ${breakpointValues.xs}px)`,
    sm: `@media (min-width: ${breakpointValues.sm}px)`,
    md: `@media (min-width: ${breakpointValues.md}px)`,
    lg: `@media (min-width: ${breakpointValues.lg}px)`,
    xl: `@media (min-width: ${breakpointValues.xl}px)`,
    xxl: `@media (min-width: ${breakpointValues.xxl}px)`,
  },
  
  // Max-width media queries (desktop-first approach)
  down: {
    xs: `@media (max-width: ${breakpointValues.sm - 1}px)`,
    sm: `@media (max-width: ${breakpointValues.md - 1}px)`,
    md: `@media (max-width: ${breakpointValues.lg - 1}px)`,
    lg: `@media (max-width: ${breakpointValues.xl - 1}px)`,
    xl: `@media (max-width: ${breakpointValues.xxl - 1}px)`,
  },
  
  // Range media queries (between two breakpoints)
  between: {
    xsAndSm: `@media (min-width: ${breakpointValues.xs}px) and (max-width: ${breakpointValues.md - 1}px)`,
    smAndMd: `@media (min-width: ${breakpointValues.sm}px) and (max-width: ${breakpointValues.lg - 1}px)`,
    mdAndLg: `@media (min-width: ${breakpointValues.md}px) and (max-width: ${breakpointValues.xl - 1}px)`,
    lgAndXl: `@media (min-width: ${breakpointValues.lg}px) and (max-width: ${breakpointValues.xxl - 1}px)`,
  },
  
  // Only specific breakpoint
  only: {
    xs: `@media (max-width: ${breakpointValues.sm - 1}px)`,
    sm: `@media (min-width: ${breakpointValues.sm}px) and (max-width: ${breakpointValues.md - 1}px)`,
    md: `@media (min-width: ${breakpointValues.md}px) and (max-width: ${breakpointValues.lg - 1}px)`,
    lg: `@media (min-width: ${breakpointValues.lg}px) and (max-width: ${breakpointValues.xl - 1}px)`,
    xl: `@media (min-width: ${breakpointValues.xl}px) and (max-width: ${breakpointValues.xxl - 1}px)`,
    xxl: `@media (min-width: ${breakpointValues.xxl}px)`,
  },
} as const;

// Device-specific media queries
export const deviceQueries = {
  // Mobile devices
  mobile: mediaQueries.down.sm,
  mobileOnly: mediaQueries.only.xs,
  mobileLandscape: `@media (max-width: ${breakpointValues.md - 1}px) and (orientation: landscape)`,
  
  // Tablet devices
  tablet: mediaQueries.between.smAndMd,
  tabletOnly: mediaQueries.only.md,
  tabletLandscape: `@media (min-width: ${breakpointValues.md}px) and (max-width: ${breakpointValues.lg - 1}px) and (orientation: landscape)`,
  
  // Desktop devices
  desktop: mediaQueries.up.lg,
  desktopOnly: mediaQueries.only.lg,
  desktopWide: mediaQueries.up.xl,
  
  // Touch devices
  touch: '@media (hover: none) and (pointer: coarse)',
  noTouch: '@media (hover: hover) and (pointer: fine)',
  
  // High DPI displays
  retina: '@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)',
  
  // Reduced motion preference
  reducedMotion: '@media (prefers-reduced-motion: reduce)',
  
  // Dark mode preference
  darkMode: '@media (prefers-color-scheme: dark)',
  lightMode: '@media (prefers-color-scheme: light)',
} as const;

// Responsive values helper type
export type ResponsiveValue<T> = {
  [K in keyof typeof breakpointValues]?: T;
};

// Type definitions
export type BreakpointValues = typeof breakpointValues;
export type Breakpoint = keyof BreakpointValues;
export type MediaQueries = typeof mediaQueries;
export type DeviceQueries = typeof deviceQueries;

// Utility functions
export const getBreakpointValue = (breakpoint: Breakpoint) => 
  breakpointValues[breakpoint];

export const getMediaQuery = (
  type: keyof MediaQueries,
  breakpoint: keyof MediaQueries[typeof type]
) => {
  return mediaQueries[type][breakpoint];
};

export const createCustomMediaQuery = (minWidth?: number, maxWidth?: number) => {
  if (minWidth && maxWidth) {
    return `@media (min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
  } else if (minWidth) {
    return `@media (min-width: ${minWidth}px)`;
  } else if (maxWidth) {
    return `@media (max-width: ${maxWidth}px)`;
  }
  return '';
};

// CSS helper for styled-components
export const responsiveMixin = {
  // Mobile-first responsive design
  up: (breakpoint: Breakpoint) => mediaQueries.up[breakpoint],
  
  // Desktop-first responsive design
  down: (breakpoint: keyof typeof mediaQueries.down) => mediaQueries.down[breakpoint],
  
  // Between two breakpoints
  between: (start: Breakpoint, end: Breakpoint) => 
    `@media (min-width: ${breakpointValues[start]}px) and (max-width: ${breakpointValues[end] - 1}px)`,
  
  // Only specific breakpoint
  only: (breakpoint: keyof typeof mediaQueries.only) => mediaQueries.only[breakpoint],
  
  // Container with max-width
  container: (breakpoint?: keyof typeof containerMaxWidths) => `
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    padding-left: ${gridSystem.containerPadding.xs};
    padding-right: ${gridSystem.containerPadding.xs};
    
    ${breakpoint && containerMaxWidths[breakpoint] ? `
      max-width: ${containerMaxWidths[breakpoint]};
    ` : ''}
    
    ${mediaQueries.up.sm} {
      padding-left: ${gridSystem.containerPadding.sm};
      padding-right: ${gridSystem.containerPadding.sm};
    }
    
    ${mediaQueries.up.md} {
      padding-left: ${gridSystem.containerPadding.md};
      padding-right: ${gridSystem.containerPadding.md};
    }
    
    ${mediaQueries.up.lg} {
      padding-left: ${gridSystem.containerPadding.lg};
      padding-right: ${gridSystem.containerPadding.lg};
    }
  `,
  
  // Hide at breakpoint
  hideAt: (breakpoint: Breakpoint) => `
    ${mediaQueries.up[breakpoint]} {
      display: none !important;
    }
  `,
  
  // Show at breakpoint
  showAt: (breakpoint: Breakpoint) => `
    display: none !important;
    
    ${mediaQueries.up[breakpoint]} {
      display: block !important;
    }
  `,
  
  // Responsive font size
  responsiveFont: (sizes: Partial<Record<Breakpoint, string>>) => {
    let css = '';
    Object.entries(sizes).forEach(([breakpoint, size]) => {
      if (breakpoint === 'xs') {
        css += `font-size: ${size};`;
      } else {
        css += `
          ${mediaQueries.up[breakpoint as Breakpoint]} {
            font-size: ${size};
          }
        `;
      }
    });
    return css;
  },
};
